import { Link } from "react-router-dom";
import { ArrowRight, Target, Users, Heart } from "lucide-react";
import { PageHero, SectionLabel } from "@/components/Section";
import { IMAGES, EVENT } from "@/data/content";
import { FoundationLink } from "@/components/FoundationLink";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        overline="Est. 2024 · Montgomery County, Texas"
        title="The Wayne"
        accent="Walker Foundation"
        subtitle="A community built on service, the outdoors, and giving back — carrying a legacy forward, one fairway at a time."
        image={IMAGES.vintageClubs}
      />

      <section className="paper-noise bg-background">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 md:py-28 relative z-10">
          <SectionLabel>Our Story</SectionLabel>
          <div className="mt-6 space-y-6 text-base md:text-lg text-muted-foreground leading-relaxed">
            <p>
              The Wayne Walker Foundation was founded in 2024 to honor a life defined by generosity, grit, and a deep love of the Texas outdoors. From the duck blind at dawn to a redfish on the line and a round of golf with friends, Wayne believed that the best moments were the ones shared — and shared for a purpose.
            </p>
            <p>
              Today, the Foundation channels that spirit into hands-on community giving across Montgomery County. We rally friends, families, and local businesses around events that are equal parts celebration and cause, directing meaningful support to the organizations that keep our community safe and strong.
            </p>
            <p>
              Our inaugural <span className="text-espresso font-semibold">Heroes & Legacy Golf Classic</span> is where it all comes together: a four-person scramble at The Woodlands Country Club — The Trails in Spring, Texas, benefiting the {EVENT.beneficiary}.
            </p>
          </div>
          <div data-testid="about-foundation-site-card" className="mt-10 border border-border bg-card p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5 hard-shadow-sm">
            <div>
              <div className="label-font text-xs text-rust">Beyond the Classic</div>
              <p className="mt-2 text-sm md:text-base text-muted-foreground leading-relaxed">
                Want the full story — programs, impact, and other ways to give year-round? Explore the Foundation's own website.
              </p>
            </div>
            <FoundationLink testId="about-foundation-site-link" className="label-font text-xs border border-espresso px-6 py-3.5 text-espresso hover:bg-espresso hover:text-cream transition-colors shrink-0 justify-center">
              Visit the Foundation
            </FoundationLink>
          </div>
        </div>
      </section>

      <section className="bg-card paper-noise">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28 relative z-10">
          <SectionLabel>What We Stand For</SectionLabel>
          <h2 className="mt-5 font-serif font-black text-4xl text-espresso">Our Values</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { icon: Heart, title: "Service First", desc: "We give back to those who give everything — starting with our first responders." },
              { icon: Users, title: "Community Roots", desc: "Every event is built by and for Montgomery County neighbors, families, and businesses." },
              { icon: Target, title: "Direct Impact", desc: "Funds raised go straight to the mission, with transparency at every step." },
            ].map((v) => (
              <div key={v.title} className="border border-border bg-background p-8 transition-transform hover:-translate-y-1 hard-shadow-sm">
                <span className="h-12 w-12 bg-rust text-primary-foreground flex items-center justify-center"><v.icon size={22} /></span>
                <h3 className="mt-5 font-serif font-bold text-2xl text-espresso">{v.title}</h3>
                <p className="mt-3 text-muted-foreground">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-espresso text-cream">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 text-center">
          <h2 className="font-serif font-black text-3xl sm:text-4xl">Be Part Of The Legacy</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/register" className="label-font text-sm bg-rust text-primary-foreground px-8 py-4 border border-cream/20 transition-transform hover:-translate-y-1 hard-shadow flex items-center gap-2">Register a Team <ArrowRight size={16} /></Link>
            <Link to="/cause" className="label-font text-sm border border-cream/40 px-8 py-4 transition-colors hover:bg-cream/10">The Cause</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
